import React from 'react';
import type { Session } from 'state';

import { formatters } from 'services';
import PriceCell from 'reusable/PriceCell';

import InstrumentActions from './InstrumentActions';
import { ordinaryQuoteIsAvailable } from './model';
import type { MarketInstrument, SelectInstrument } from './types';
import type { ExecutionIntent } from './executionModel';
import { executionTargetsInstrument } from './executionModel';

type Props = {
  rows: MarketInstrument[];
  session: Session;
  reportingCurrencySymbol: string;
  selectedInstrumentId?: number;
  selectInstrument: SelectInstrument;
  showOrderModal: (instrumentId: number) => void;
  showODAModal: (instrumentId: number) => void;
  showETFDetails?: (assetId: number) => void;
  kind: 'fx' | 'etf';
  executionIntent: ExecutionIntent;
};

const pnlClass = (value: number) =>
  value > 0 ? 'is-positive' : value < 0 ? 'is-negative' : '';

const SpotMarkets: React.FC<Props> = ({
  rows,
  session,
  reportingCurrencySymbol,
  selectedInstrumentId,
  selectInstrument,
  showOrderModal,
  showODAModal,
  showETFDetails,
  kind,
  executionIntent,
}) => (
  <div className={`markets-v2-spot-grid markets-v2-spot-grid--${kind}`}>
    {rows.map(row => {
      const hasQuote = ordinaryQuoteIsAvailable(row.asset);
      const selected = selectedInstrumentId === row.instrument.id;
      const focus = () => selectInstrument(row.instrument);
      const inDesk = executionTargetsInstrument(
        executionIntent,
        row.instrument.id,
      );
      const positionUnit =
        kind === 'fx'
          ? row.currencySymbol
          : row.instrument.amount === 1
            ? 'unit'
            : 'units';

      return (
        <article
          key={row.instrument.id}
          className={`markets-v2-spot-card${selected ? ' is-selected' : ''}${
            inDesk ? ' is-in-desk' : ''
          }${hasQuote ? '' : ' is-unquoted'}`}
          onClick={focus}
          aria-label={`${row.asset.name || row.asset.ticker} market card`}
        >
          <div className="markets-v2-spot-card__top">
            <div className="markets-v2-instrument-id">
              <span className="markets-v2-ticker">{row.asset.ticker}</span>
              <h3 title={row.asset.name}>
                {row.asset.name || row.asset.ticker}
              </h3>
              <span className="markets-v2-instrument-sub">
                {kind === 'etf'
                  ? `${row.asset.geography || 'Global'} · ${row.currencySymbol}`
                  : `${row.currencySymbol} spot`}
              </span>
            </div>
            <span
              className={`markets-v2-quote-state ${
                hasQuote ? 'is-live' : 'is-waiting'
              }`}
            >
              <i />
              {hasQuote
                ? row.marketIsOpen
                  ? 'Quoted'
                  : 'Market closed'
                : 'Awaiting quote'}
            </span>
          </div>

          <div className="markets-v2-two-way" aria-label="Bid and ask quote">
            <div>
              <span>Bid</span>
              <strong>
                {hasQuote ? (
                  <PriceCell
                    price={formatters.priceFormat(
                      row.asset.bid,
                      row.asset.ticker,
                      row.currencySymbol,
                      row.asset.resourcetype,
                    )}
                    priceDiff={row.asset.bid_diff}
                  />
                ) : (
                  '—'
                )}
              </strong>
            </div>
            <div>
              <span>Ask</span>
              <strong>
                {hasQuote ? (
                  <PriceCell
                    price={formatters.priceFormat(
                      row.asset.ask,
                      row.asset.ticker,
                      row.currencySymbol,
                      row.asset.resourcetype,
                    )}
                    priceDiff={row.asset.ask_diff}
                  />
                ) : (
                  '—'
                )}
              </strong>
            </div>
          </div>

          <dl className="markets-v2-position-strip">
            <div>
              <dt>Position</dt>
              <dd>
                {formatters.commasNoDigits(row.instrument.amount)}{' '}
                {positionUnit}
              </dd>
            </div>
            <div>
              <dt>Value · {reportingCurrencySymbol}</dt>
              <dd>{formatters.commasNoDigits(row.reportingValue)}</dd>
            </div>
            <div>
              <dt>Unrealized</dt>
              <dd className={pnlClass(row.instrument.reporting_unrealized_pnl)}>
                {formatters.commasNoDigits(
                  row.instrument.reporting_unrealized_pnl,
                )}
              </dd>
            </div>
          </dl>

          <InstrumentActions
            row={row}
            session={session}
            selected={selected}
            onFocus={focus}
            onTrade={() => {
              focus();
              showOrderModal(row.instrument.id);
            }}
            onODA={() => {
              focus();
              showODAModal(row.instrument.id);
            }}
            onDetails={
              showETFDetails ? () => showETFDetails(row.asset.id) : undefined
            }
            tradeOpen={inDesk && executionIntent.kind === 'order'}
            odaOpen={inDesk && executionIntent.kind === 'oda'}
          />
        </article>
      );
    })}
  </div>
);

export default SpotMarkets;
